import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { FormRow, Group, Notice, Row } from '@store/ui';
import { ApiError } from '../../api';
import { haptic } from '../../telegram';

/**
 * The form pieces the management screens share.
 *
 * Nothing here draws a box. `FormGroup` is the surface and the rows sit inside
 * it with hairlines between them, which is the same iOS grouped list the rest of
 * the app uses — a card per field is what this design system already rejected
 * twice.
 */

/*
 * A bare glyph that is still a 36px target.
 *
 * Inline for the same reason the button's spinner is: an icon control that
 * collapses to the size of its own path is a control nobody can hit with a
 * thumb, and that must not depend on which stylesheet the screen lands in. The
 * class name rides along for the CSS agent to give it a pressed state.
 */
export const ICON_BUTTON: CSSProperties = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 10,
  background: 'transparent',
  border: 0,
  color: 'var(--text-secondary)',
  cursor: 'pointer',
};

export const ICON_BUTTON_DANGER: CSSProperties = { ...ICON_BUTTON, color: 'var(--danger)' };

export interface TextRowProps {
  readonly label: ReactNode;
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'text' | 'decimal' | 'numeric' | 'url';
  readonly maxLength?: number;
  readonly invalid?: boolean;
  readonly trailing?: ReactNode;
}

export function TextRow({
  label,
  id,
  value,
  onChange,
  placeholder,
  inputMode,
  maxLength,
  invalid,
  trailing,
}: TextRowProps) {
  return (
    <FormRow label={label} htmlFor={id}>
      <input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {trailing}
    </FormRow>
  );
}

/**
 * A multi-line field, label above.
 *
 * `--stacked` and a bare `<textarea>`: the group already is the surface, and a
 * textarea that brings its own rounded box was a reported defect — the capsule
 * blob on the support screen is the same mistake one round earlier.
 */
export function AreaRow({
  label,
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
}: {
  readonly label: ReactNode;
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly maxLength?: number;
}) {
  return (
    <FormRow label={label} htmlFor={id} stacked>
      <textarea
        id={id}
        value={value}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormRow>
  );
}

/**
 * One of N, as rows with a tick.
 *
 * A `<select>` opens the platform's own wheel, which on iOS covers the form the
 * owner is halfway through and hands back a value with no record of what the
 * other options were. Four rows in a group are readable at a glance and are the
 * same gesture as picking a variant on the product screen.
 */
export function ChoiceRows<T extends string>({
  options,
  value,
  onChange,
}: {
  readonly options: ReadonlyArray<{ value: T; label: string; subtitle?: string }>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}) {
  return (
    <Group>
      {options.map((option) => (
        <Row
          key={option.value}
          title={option.label}
          {...(option.subtitle ? { subtitle: option.subtitle } : {})}
          selected={option.value === value}
          onClick={() => {
            haptic.select();
            onChange(option.value);
          }}
        />
      ))}
    </Group>
  );
}

/** A boolean, as a row that is either ticked or not. */
export function ToggleRow({
  title,
  subtitle,
  value,
  onChange,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
}) {
  return (
    <Row
      title={title}
      {...(subtitle ? { subtitle } : {})}
      selected={value}
      onClick={() => {
        haptic.select();
        onChange(!value);
      }}
    />
  );
}

/** The heading every management screen opens with. */
export function AdminHeader({
  title,
  subtitle,
}: {
  readonly title: string;
  readonly subtitle?: string | null;
}) {
  return (
    <header>
      <h1 className="title">{title}</h1>
      {subtitle ? (
        <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}

/**
 * A save that cannot be run twice and never eats the reason it failed.
 *
 * The ref is the guard, not `busy`: a second press lands in the same tick as
 * the first, before React has re-rendered with the disabled button, and two
 * products get created. The message is whatever the server said — replacing it
 * with «Что-то пошло не так» when there was a real sentence is the complaint
 * this whole section exists to answer.
 *
 * Nothing in here touches form state. A failed save leaves every field exactly
 * as the owner typed it, which is the caller's job to preserve and this hook's
 * job not to interfere with.
 */
export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const run = async (task: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);

    try {
      haptic.impact('medium');
      await task();
    } catch (cause) {
      haptic.error();
      setError(
        cause instanceof ApiError ? cause.message : 'Не удалось сохранить. Попробуйте ещё раз.',
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return { busy, error, setError, run };
}

/**
 * The bar the save button lives in, with the failure directly above it.
 *
 * Above the bar rather than at the top of the screen: a reason the owner has to
 * scroll back to find is a reason they will press the button again without
 * reading.
 */
export function ActionBar({ error, children }: { error: string | null; children: ReactNode }) {
  return (
    <div className="action-bar">
      {error ? (
        <div style={{ marginBottom: 'var(--space-3)' }} role="alert">
          <Notice tone="warning">{error}</Notice>
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** A list of things that are wrong, under the group they are wrong about. */
export function Problems({ items }: { readonly items: readonly string[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      <Notice tone="warning">
        {items.length === 1 ? (
          items[0]
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </Notice>
    </div>
  );
}
